import API from "utils/API";
import Loan from "typedef/Loan";
import { useState, useEffect, useMemo } from "react";
import PricingEngine from "typedef/PricingEngine";
import getMaxCLTVHELOC from "screens/BorrowerInviteRegisterForm/getMaxCLTVHELOC";
import { getUseOfProceedsAdjuster } from "utils/getUseOfProceedsAdjuster";

export type Marks = Loan["initialOffer"][];

const useOffer = () => {
  const [loan, setLoan] = useState<Loan>();
  const [loading, setLoading] = useState(true);
  const [pricingEngine, setPricingEngine] = useState<PricingEngine>();
  const [requestedLoanAmount, setRequestedLoanAmount] = useState(0);
  const [maxLoanAmount, setMaxLoanAmount] = useState(0);
  const [currMaxCreditScoreValue, setCurrMaxCreditScoreValue] = useState(0);
  const [currentLoanBalance, setCurrentLoanBalance] = useState(0);
  const [homeValue, setHomeValue] = useState(0);

  useEffect(() => {
    API.get<PricingEngine>(`/get-heloc-pricing-engine`).then((result) => {
      if ("error" in result) {
        return;
      } else {
        setPricingEngine(result.data);
      }
    });
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const loanResponse = await API.get<Loan>(`/get/my-loan`);
      if (!("error" in loanResponse)) setLoan(loanResponse.data);

      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (loan && pricingEngine) {
      const loanAmount = loan.initialOffer.amount;
      const creditScore = loan.initialOffer.creditScore;
      const loanBalance = loan.initialOffer.currentLoanBalance;
      const homeValue = loan.property.homeValue;
      const maxCLTV = getMaxCLTVHELOC(pricingEngine, creditScore);
      const calculatedMaxLoanAmount = homeValue * maxCLTV - loanBalance;
      const selectedRange = pricingEngine.rangesCreditScore.find((range) => {
        return range.min === creditScore;
      });
      setRequestedLoanAmount(loanAmount);
      setMaxLoanAmount(calculatedMaxLoanAmount);
      setCurrMaxCreditScoreValue(selectedRange?.max as number);
      setCurrentLoanBalance(loanBalance);
      setHomeValue(homeValue);
    }
  }, [loan, pricingEngine]);

  const offertOptionsByFico = useMemo(() => {
    if (
      (!requestedLoanAmount || !maxLoanAmount || !currMaxCreditScoreValue) &&
      loading
    )
      return [];
    if (!pricingEngine) {
      return;
    }

    const indexCreditScore = getIndexCreditScore(
      pricingEngine,
      currMaxCreditScoreValue,
    );

    const rowsNumber =
      pricingEngine?.margin?.[indexCreditScore]?.filter((el) => el !== null)
        .length ?? 0;

    const offers: {
      maxLoanAmount: number;
      rate: number | undefined;
    }[] = [];

    for (let index = 0; index < rowsNumber; index++) {
      const currMaxCltv = pricingEngine?.rangesCltv[index]?.max;

      const newRequestedLoanAmount =
        currMaxCltv * homeValue - currentLoanBalance;

      const marginRate = getMarginRate({
        pricingEngine: pricingEngine,
        cltv: currMaxCltv,
        creditScore: currMaxCreditScoreValue,
      });

      const loanPurposeValue = getUseOfProceedsAdjuster(
        pricingEngine?.marginAdjusters?.loanPurpose,
        loan?.initialOffer?.useProceeds ?? "Home Improvement",
      );

      let interestRateValue;

      if (pricingEngine?.primeRate !== undefined)
        if (
          currentLoanBalance === 0 &&
          pricingEngine?.firstLienDiscount &&
          marginRate
        ) {
          interestRateValue =
            marginRate +
            loanPurposeValue +
            pricingEngine?.primeRate -
            pricingEngine?.firstLienDiscount;
        } else {
          interestRateValue =
            (marginRate ?? 0) + pricingEngine?.primeRate + loanPurposeValue;
        }

      if (newRequestedLoanAmount > requestedLoanAmount) {
        if (currentLoanBalance === 0) {
          if (newRequestedLoanAmount < pricingEngine?.loanMaxFirstLien) {
            offers.push({
              maxLoanAmount: newRequestedLoanAmount,
              rate: interestRateValue,
            });
          } else if (
            !offers?.some(
              (offer) =>
                offer.maxLoanAmount === pricingEngine?.loanMaxFirstLien,
            )
          ) {
            offers.push({
              maxLoanAmount: pricingEngine?.loanMaxFirstLien,
              rate: interestRateValue,
            });
          }
        } else if (newRequestedLoanAmount < pricingEngine?.loanMax) {
          offers.push({
            maxLoanAmount: newRequestedLoanAmount,
            rate: interestRateValue,
          });
        } else if (
          !offers?.some(
            (offer) => offer.maxLoanAmount === pricingEngine?.loanMax,
          )
        ) {
          offers.push({
            maxLoanAmount: pricingEngine?.loanMax,
            rate: interestRateValue,
          });
        }
      }
    }

    return offers;
  }, [
    maxLoanAmount,
    currentLoanBalance,
    homeValue,
    loading,
    pricingEngine,
    currMaxCreditScoreValue,
    requestedLoanAmount,
    loan?.initialOffer.useProceeds,
  ]);

  return {
    offertOptionsByFico,
  };
};

export default useOffer;

const getIndexCreditScore = (
  pricingEngine: PricingEngine,
  creditScore: number,
) => {
  return pricingEngine?.rangesCreditScore?.findIndex(
    (item) => creditScore >= item.min && creditScore <= item.max,
  );
};

const getMarginRate = ({
  pricingEngine,
  cltv,
  creditScore,
}: {
  pricingEngine: PricingEngine;
  cltv: number;
  creditScore: number;
}) => {
  const indexCS = getIndexCreditScore(pricingEngine, creditScore);

  const indexCltv = pricingEngine?.rangesCltv?.findIndex((item) => {
    if (item.min === 0) {
      return (
        Number(cltv.toFixed(2)) >= item.min &&
        Number(cltv.toFixed(2)) <= item.max
      );
    } else {
      return (
        Number(cltv.toFixed(2)) > item.min &&
        Number(cltv.toFixed(2)) <= item.max
      );
    }
  });

  let marginRate = null;
  const margin = pricingEngine?.margin;
  if (indexCS !== -1 && indexCltv !== -1) {
    marginRate = margin?.[indexCS][indexCltv];
  }

  return marginRate;
};
